import { useCallback, useEffect, useRef, useState } from 'react';

const normalizeCommunityId = value => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = Number(value);
  return Number.isNaN(parsed) ? value : parsed;
};

const communityMatchesRef = (community, ref) => {
  if (community == null || ref == null || ref === '') {
    return false;
  }
  const refStr = String(ref);
  if (community.id != null && String(community.id) === refStr) {
    return true;
  }
  if (community.documentId != null && String(community.documentId) === refStr) {
    return true;
  }
  return false;
};

const publicCommunityRef = community => {
  if (!community) {
    return null;
  }
  if (community.documentId) {
    return community.documentId;
  }
  return community.id;
};

const publicEntityRef = entity => {
  if (entity == null || entity === '') {
    return null;
  }
  if (typeof entity !== 'object') {
    return entity;
  }
  if (entity.documentId != null && String(entity.documentId).trim() !== '') {
    return entity.documentId;
  }
  return entity.id ?? null;
};

const useResolvedCommunitySelection = ({
  urlCommunityId,
  getCommunities,
  onFirstCommunityResolved,
  requireAdminAccess = false,
}) => {
  const normalizedUrlCommunityId = normalizeCommunityId(urlCommunityId);
  const [selectedCommunityId, setSelectedCommunityId] = useState(normalizedUrlCommunityId);
  const [resolvingInitialCommunity, setResolvingInitialCommunity] = useState(
    requireAdminAccess || !normalizedUrlCommunityId
  );
  const [accessDenied, setAccessDenied] = useState(false);
  const [adminCommunities, setAdminCommunities] = useState([]);
  const skipAutoResolveRef = useRef(!!normalizedUrlCommunityId && !requireAdminAccess);

  useEffect(() => {
    if (!requireAdminAccess) {
      if (normalizedUrlCommunityId) {
        setSelectedCommunityId(normalizedUrlCommunityId);
        setResolvingInitialCommunity(false);
        setAccessDenied(false);
        skipAutoResolveRef.current = true;
        return;
      }
      setSelectedCommunityId(null);
      if (skipAutoResolveRef.current) {
        setResolvingInitialCommunity(false);
        return;
      }
      setResolvingInitialCommunity(true);
      return;
    }

    let isCancelled = false;
    const verifyAdminAccess = async () => {
      setResolvingInitialCommunity(true);
      setAccessDenied(false);
      try {
        const data = await getCommunities();
        if (isCancelled) return;
        const list = Array.isArray(data) ? data : data?.data || [];
        setAdminCommunities(list);

        if (normalizedUrlCommunityId != null) {
          const match = list.find(community =>
            communityMatchesRef(community, normalizedUrlCommunityId)
          );
          if (!match) {
            setSelectedCommunityId(normalizedUrlCommunityId);
            setAccessDenied(true);
          } else {
            setSelectedCommunityId(publicCommunityRef(match) ?? match.id);
            setAccessDenied(false);
          }
          skipAutoResolveRef.current = true;
          return;
        }

        if (list.length > 0) {
          const firstCommunity = list[0];
          setSelectedCommunityId(publicCommunityRef(firstCommunity) ?? firstCommunity.id);
          if (onFirstCommunityResolved) {
            await onFirstCommunityResolved(firstCommunity);
          }
        } else {
          setSelectedCommunityId(null);
        }
        skipAutoResolveRef.current = true;
      } catch {
        if (!isCancelled && normalizedUrlCommunityId != null) {
          setAccessDenied(true);
        }
      } finally {
        if (!isCancelled) {
          setResolvingInitialCommunity(false);
        }
      }
    };

    verifyAdminAccess();
    return () => {
      isCancelled = true;
    };
  }, [normalizedUrlCommunityId, getCommunities, onFirstCommunityResolved, requireAdminAccess]);

  useEffect(() => {
    if (requireAdminAccess) {
      return;
    }
    if (normalizedUrlCommunityId || skipAutoResolveRef.current) {
      return;
    }

    let isCancelled = false;
    const resolveFirstCommunity = async () => {
      try {
        const data = await getCommunities();
        if (isCancelled) return;
        const list = Array.isArray(data) ? data : data?.data || [];
        if (list.length > 0) {
          const firstCommunity = list[0];
          setSelectedCommunityId(publicCommunityRef(firstCommunity) ?? firstCommunity.id);
          if (onFirstCommunityResolved) {
            await onFirstCommunityResolved(firstCommunity);
          }
        }
      } finally {
        if (!isCancelled) {
          skipAutoResolveRef.current = true;
          setResolvingInitialCommunity(false);
        }
      }
    };

    resolveFirstCommunity();
    return () => {
      isCancelled = true;
    };
  }, [normalizedUrlCommunityId, getCommunities, onFirstCommunityResolved, requireAdminAccess]);

  const selectCommunity = useCallback((communityId, community) => {
    setAccessDenied(false);
    const nextRef = publicCommunityRef(community) ?? communityId;
    setSelectedCommunityId(nextRef);
    skipAutoResolveRef.current = true;
    return nextRef;
  }, []);

  return {
    selectedCommunityId,
    setSelectedCommunityId,
    resolvingInitialCommunity,
    accessDenied,
    adminCommunities,
    selectCommunity,
    publicCommunityRef,
    publicEntityRef,
  };
};

export { communityMatchesRef, publicCommunityRef, publicEntityRef, normalizeCommunityId };
export default useResolvedCommunitySelection;
